import React, { useState, useEffect } from 'react';
import { DefaultModalProps, withLocationStateModal } from 'utils/modals';

import {
  Box,
  Button,
  TextField,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  DialogActions,
} from '@mui/material';

import { useSearchTalentsLazyQuery } from '@libs/graphql-types';
import { DefaultModal } from '@libs/ui/components/modals';

import { SmsConversationType } from '../types';
import { SmsAppModals } from './types';

interface StartConversationModalProps extends DefaultModalProps<undefined> {
  onSelectTalent: (conversation: SmsConversationType) => void;
}

const StartConversationModalComponent: React.FC<
  StartConversationModalProps
> = ({ close, isOpen, onSelectTalent }) => {
  const [searchText, setSearchText] = useState('');

  const [searchTalents, { data, loading }] = useSearchTalentsLazyQuery({
    fetchPolicy: 'network-only',
  });

  const handleClose = () => {
    close();
    setSearchText('');
  };

  // Debounced automatic search
  useEffect(() => {
    if (searchText.trim().length > 2) {
      const timeoutId = setTimeout(() => {
        searchTalents({
          variables: {
            search: searchText.trim(),
            first: 10,
            source_type: 'OWN',
            is_active: true,
            is_verification_required: false,
          },
        });
      }, 500); // 500ms debounce

      return () => clearTimeout(timeoutId);
    }
  }, [searchText, searchTalents]);

  const handleSelectTalent = (talent: any) => {
    // Create a mock conversation structure for the selected talent
    const mockConversation: SmsConversationType = {
      __typename: 'SMSConversation',
      talent: {
        __typename: 'Talent',
        id: talent.id,
        first_name_abac: talent.first_name_abac,
        last_name_abac: talent.last_name_abac,
        avatar: talent.avatar,
        talent_data: talent.talent_data,
      },
      last_sms: {
        __typename: 'SMS',
        id: 'new',
        content: undefined,
        created_at: new Date().toISOString(),
        delivered_at: undefined,
        status: 'new' as any,
        sender_type: 'user' as any,
        error_message: undefined,
        talent_phone_number: talent.talent_data?.phone || '',
        company_id: '',
        talent_id: talent.id,
        user_id: '',
      },
    };

    onSelectTalent(mockConversation);
    handleClose();
  };

  const talents = data?.talentsSearch?.data || [];

  return (
    <DefaultModal
      open={isOpen}
      handleClose={handleClose}
      title="Start New SMS Conversation"
      maxWidth="sm"
      fullWidth
      actions={
        <DialogActions>
          <Button color="info" size="large" onClick={handleClose}>
            Cancel
          </Button>
        </DialogActions>
      }
    >
      {/* Fixed Search Form */}
      <Box sx={{ mt: 1, mb: 2 }}>
        <TextField
          autoFocus
          fullWidth
          variant="filled"
          label="Search talents by name, email, or skills"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          helperText={
            searchText.length > 0 && searchText.length <= 2
              ? 'Type at least 3 characters to search'
              : ''
          }
        />
      </Box>

      {/* Scrollable Results Area */}
      <Box
        sx={{
          height: '400px',
          overflowY: 'auto',
        }}
      >
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress color="info" />
          </Box>
        )}

        {!loading && talents.length === 0 && searchText && (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography color="text.secondary">
              No talents found. Try a different search.
            </Typography>
          </Box>
        )}

        {!loading && talents.length > 0 && (
          <List sx={{ pt: 0, pb: 0 }}>
            {talents.map((item) => {
              const talent = item?.talent;
              if (!talent) return null;

              const firstName = talent.first_name_abac?.value || '';
              const lastName = talent.last_name_abac?.value || '';
              const fullName = `${firstName} ${lastName}`.trim() || 'Unknown';
              const email = talent.email_abac?.value || '';
              const phone = talent.talent_data?.phone || 'No phone';
              const avatar = talent.avatar?.avatar;

              return (
                <ListItem
                  key={talent.id}
                  button
                  onClick={() => handleSelectTalent(talent)}
                  sx={{
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar src={avatar || undefined}>
                      {fullName.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={fullName}
                    secondary={
                      <>
                        {email && (
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            display="block"
                          >
                            {email}
                          </Typography>
                        )}
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                        >
                          {phone}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    </DefaultModal>
  );
};

export const StartConversationModal = withLocationStateModal<undefined>({
  id: SmsAppModals.START_CONVERSATION,
})(StartConversationModalComponent);
